import { useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Path segments that look like record ids (numeric, or long hex/uuid-ish). Masked out before the
 * route is ever sent to the API so no tenant data leaks into the help agent's context.
 */
const ID_SEGMENT = /^(\d+|[0-9a-f]{8,}|[0-9a-f-]{16,})$/i;

/** Turn a resolved pathname into a param-free route pattern, e.g. /app/lists/detail/8f3a → …/:id. */
export const sanitizeRoutePattern = (pathname: string): string =>
	pathname
		.split("/")
		.map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
		.join("/");

/** Longest-prefix → human label map used to tell the agent which page the user is on. */
const PAGE_LABELS: { prefix: string; label: string }[] = [
	{ prefix: "/app/lead-scoring", label: "Lead Scoring" },
	{ prefix: "/app/lists", label: "Lead Lists" },
	{ prefix: "/app/contacts", label: "Contacts" },
	{ prefix: "/app/do-not-contact", label: "Do Not Contact" },
	{ prefix: "/app/analytics", label: "Analytics" },
	{ prefix: "/app/signals", label: "Signals" },
	{ prefix: "/app/knowledge-db", label: "Knowledge DB" },
	{ prefix: "/app/copywriting", label: "Copywriting" },
	{ prefix: "/app/templates", label: "Templates" },
	{ prefix: "/app/messages", label: "Messages" },
	{ prefix: "/app/profile-tracker", label: "Profile Tracker" },
	{ prefix: "/app/integrations", label: "Integrations" },
	{ prefix: "/app/dashboard", label: "Dashboard" },
];

/** Human-readable label for the current page, or undefined if we don't have one mapped. */
export const getPageLabel = (pathname: string): string | undefined =>
	[...PAGE_LABELS]
		.sort((a, b) => b.prefix.length - a.prefix.length)
		.find((entry) => pathname.startsWith(entry.prefix))?.label;

export interface HelpContext {
	pathname: string;
	/** Param-free route pattern, safe to send to the API. */
	routePattern: string;
	/** Human-readable page label, if mapped. */
	label?: string;
}

/**
 * The current page, as context for the assistant. Route *pattern* and label only: the assistant
 * answers from the published help center, which this app holds no copy of, so there is nothing
 * local to match a route against.
 */
export const useHelpContext = (): HelpContext => {
	const { pathname } = useLocation();

	return useMemo(
		() => ({
			pathname,
			routePattern: sanitizeRoutePattern(pathname),
			label: getPageLabel(pathname),
		}),
		[pathname]
	);
};
